import { ReplyToMessageActionOptions } from './actions/replyToMessage';
import { ComposeOperation, default as ComposeViewState } from './store/schema/ComposeViewState';
import safelyGetSmartReplyExtractionId from './utils/safelyGetSmartReplyExtractionId';
import { isGroupScenario } from 'owa-mail-compose-store/lib/utils/viewStateUtils';
import { ActionSource } from 'owa-mail-store';

export { ComposeOperation };

export default {
    MailComposeReply: {
        datapoint: {
            name: 'MailComposeReply',
            options: { isQos: true },
            customData: (options: ReplyToMessageActionOptions) => [
                options.actionSource,
                options.useFullCompose,
            ],
            actionSource: (options: ReplyToMessageActionOptions) => options.actionSource,
            cosmosOnlyData: (options: ReplyToMessageActionOptions) => options.referenceItemId,
        },
    },
    MailComposeReplyAll: {
        datapoint: {
            name: 'MailComposeReplyAll',
            options: { isQos: true },
            customData: (options: ReplyToMessageActionOptions) => [
                options.actionSource,
                options.useFullCompose,
            ],
            actionSource: (options: ReplyToMessageActionOptions) => options.actionSource,
            cosmosOnlyData: (options: ReplyToMessageActionOptions) => options.referenceItemId,
        },
    },
    MailComposeForward: {
        datapoint: {
            name: 'MailComposeForward',
            options: { isQos: true },
            customData: (referenceItemIdStr: string, actionSource: ActionSource) => [actionSource],
            actionSource: (referenceItemIdStr: string, actionSource: ActionSource) => actionSource,
            cosmosOnlyData: (referenceItemIdStr: string, actionSource: ActionSource) =>
                referenceItemIdStr,
        },
    },
    // MailComposeSend captures the server related javascript,
    // from pre-JS prior to invoking the service to post-JS validating the server response.
    MailComposeSend: {
        datapoint: {
            name: 'MailComposeSend',
            options: {
                // isQos disables sampling
                isQos: true,
            },
            customData: (
                showErrorToUser?: boolean,
                isAsyncSend?: boolean,
                messageText?: string
            ) => [showErrorToUser, isAsyncSend, null /* TODO: 24239 */],
        },
    },
    AppendOnSendEvent: {
        datapoint: {
            name: 'MailComposeAppendOnSend',
            customData: (location?: string, bodyType?: string) => {
                return {
                    owa_1: location, // either 'Div' or 'End'
                    owa_2: bodyType, // either 'HTML' or 'Text'
                    name,
                };
            },
        },
    },

    GroupMailComposeSend: {
        datapoint: {
            name: 'GroupMailComposeSend',
            options: {
                // isQos disables sampling
                isQos: true,
            },
            customData: (
                viewState: ComposeViewState,
                showErrorToUser?: boolean,
                isAsyncSend?: boolean
            ) => ({
                showErrorToUser: showErrorToUser,
                isAsyncSend: isAsyncSend,
                composeTraceId: viewState.logTraceId,
                operation: viewState.operation,
            }),
        },
    },

    // MailComposeSendE2E captures all send relating javascript across all scenarios.
    // This includes async/sync/delayed send scenarios and is 100% javascript coverage.
    MailComposeSendE2E: {
        datapoint: {
            name: 'MailComposeSendE2E',
            options: {
                // This is temporary to get data in MSIT. Should be removed before starting Prod rollout
                // https://msfast.visualstudio.com/FAST/Office%20People%20HD/_workitems/edit/253222
                isCore: true,
            },
            customData: (viewState: ComposeViewState) => ({
                composeTraceId: viewState.logTraceId,
                operation: viewState.operation,
                isGroupScenario: isGroupScenario(viewState),
            }),
        },
    },
    MailComposeSendRequestReadReceipt: {
        datapoint: {
            name: 'MailComposeSendRequestReadReceipt',
        },
    },
    MailComposeSendWithIRMData: {
        datapoint: {
            name: 'MailComposeSendWithIRMData',
        },
    },
    MailComposeSendRequestDeliveryReceipt: {
        datapoint: {
            name: 'MailComposeSendRequestDeliveryReceipt',
        },
    },
    MailComposeSendWithMessageSensitivity: {
        datapoint: {
            name: 'MailComposeSendWithMessageSensitivity',
        },
    },
    MailComposeSave: { datapoint: { name: 'MailComposeSave' } },
    MailComposeUpConvert: { datapoint: { name: 'MailComposeUpConvert' } },

    ComposeCommandDiscardCompose: {
        datapoint: {
            name: 'ComposeCommandDiscardCompose',
            customData: (viewState: ComposeViewState) => ({
                composeTraceId: viewState.logTraceId,
                wasEmptyBody: viewState.initialContent === viewState.content,
                wasEmptySubject: viewState.subject === '',
            }),
            options: {
                // TODO: 36033 Re-enable sampling after smart reply experiment
                isCore: true,
            },
            cosmosOnlyData: (viewState: ComposeViewState) => {
                return JSON.stringify({
                    smartReplyExtractionId: safelyGetSmartReplyExtractionId(viewState),
                });
            },
        },
    },

    ComposeCommandShowFrom: {
        datapoint: {
            name: 'ComposeCommandShowFrom',
        },
    },

    ComposeCommandSetImportance: {
        datapoint: {
            name: 'ComposeCommmandSetImportance',
            customData: (viewState: any, importance: any) => [importance],
        },
    },

    SmartReplySendMessage: {
        datapoint: {
            name: 'SmartReplySendMessage',
            customData: (eid: string) => [eid],
        },
    },

    SmartReplySaveMessage: {
        datapoint: {
            name: 'SmartReplySaveMessage',
            customData: (eid: string) => [eid],
        },
    },

    SmartReplyDiscardMessage: {
        datapoint: {
            name: 'SmartReplyDiscardMessage',
            customData: (eid: string) => [eid],
        },
    },

    // Logged when an upgrade compose operation begins
    UpgradeCompose: {
        datapoint: {
            name: 'UpgradeCompose',
        },
    },

    // The upgrade timer timed out, meaning the user was notified with an info bar that the quoted body will be loaded
    // The request to get the quoted body is still pending
    UpgradeComposeTimedOut: {
        datapoint: {
            name: 'UpgradeComposeTimedOut',
        },
    },

    // Logged when upgrade compose is completed, whether succesfully loaded or skipped
    // When upgrade is loaded, this means the quoted body has successfully been appended
    // When upgrade is skipped, this means the request to receive the quoted body failed, or the user clicked send before upgrade was completed
    UpgradeComposeCompleted: {
        datapoint: {
            name: 'UpgradeComposeCompleted',
            customData: (loaded: boolean, reasonNotLoaded?: string) => [loaded, reasonNotLoaded],
        },
    },

    // Logged when the message is sent (after delay send, but before the server verifies that the message was indeed sent)
    AttachmentCountOnMessageSent: {
        datapoint: {
            name: 'AttachmentCountsOnMessageSent',
            customData: (
                classicAttachmentCount: number,
                oneDriveProLinkCount: number,
                cloudyAttachmentCount: number
            ) => [classicAttachmentCount, oneDriveProLinkCount, cloudyAttachmentCount],
        },
    },
};
